/* =====================================================================
 * StorageToolbar — 브레드크럼 + 검색 + 결과 카운트
 * ===================================================================== */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import styled from 'styled-components';
import {
  ToolbarWrap,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbSep,
  SearchWrap,
  SearchIcon,
  SearchInput,
  SectionIcon,
} from './storage.styles';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface BreadcrumbEntry {
  id: string;
  label: string;
  icon?: React.ElementType;
  iconColor?: string;
}

interface Props {
  breadcrumbs: BreadcrumbEntry[];
  searchQuery: string;
  fileCount: number;
  sidebarCollapsed?: boolean;
  onMobileBack?: () => void;
  onNavigate: (id: string) => void;
  onSearchChange: (q: string) => void;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const StorageToolbar: React.FC<Props> = ({
  breadcrumbs,
  searchQuery,
  fileCount,
  sidebarCollapsed,
  onMobileBack,
  onNavigate,
  onSearchChange,
}) => {
  const { t } = useTranslation('common');
  return (
    <ToolbarWrap $sidebarCollapsed={sidebarCollapsed}>
      {onMobileBack && (
        <MobileBackBtn type="button" onClick={onMobileBack}>
          <ChevronLeft size={18} />
        </MobileBackBtn>
      )}

      {/* Breadcrumb */}
      <Breadcrumb>
        {breadcrumbs.map((crumb, idx) => {
          const isLast = idx === breadcrumbs.length - 1;
          const CrumbIcon = crumb.icon;
          return (
            <React.Fragment key={crumb.id ?? `crumb-${idx}`}>
              {idx > 0 && (
                <BreadcrumbSep>
                  <ChevronRight size={12} />
                </BreadcrumbSep>
              )}
              <BreadcrumbItem
                $isLast={isLast}
                onClick={() => !isLast && onNavigate(crumb.id)}
                disabled={isLast}
              >
                {CrumbIcon && (
                  <SectionIcon $color={crumb.iconColor} style={{ marginRight: 4, display: 'inline-flex' }}>
                    <CrumbIcon size={14} />
                  </SectionIcon>
                )}
                {crumb.label}
              </BreadcrumbItem>
            </React.Fragment>
          );
        })}
      </Breadcrumb>

      {/* Search */}
      <SearchWrap>
        <SearchIcon>
          <Search size={14} />
        </SearchIcon>
        <SearchInput
          placeholder={t('search.placeholder')}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </SearchWrap>
      <CountBadge>{t('storage.files', { count: fileCount })}</CountBadge>
    </ToolbarWrap>
  );
};

const MobileBackBtn = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
  }
`;

const CountBadge = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`;
